import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Facebook, Mail, Link2, Check, MessageCircle, MessagesSquare, Smartphone, Twitter, Linkedin, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";

const MENU_W = 224; // w-56

// Menu déroulant de partage compact (miniatures de catalogues) : reprend toutes les
// options de ShareButtons dans un panneau au clic, sans quitter la carte.
// Le panneau est rendu en PORTAIL (document.body, position:fixed) : les cartes ont
// overflow-hidden, ce qui coupait le texte du menu sur petit écran.
export function ShareMenu({ url, title, testid = "share-menu" }) {
  const { t, ts } = useLang();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState(null); // {top|bottom, left} en coordonnées viewport
  const ref = useRef(null);
  const enc = encodeURIComponent;
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  // sms: muet sur ordinateur → mobile/tactile seulement (iPad : UA « Macintosh »)
  const isMobile = typeof navigator !== "undefined"
    && (/Android|iPhone|iPad|Mobile/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1);
  const emailShare = (e) => {
    e.stopPropagation();
    try { navigator.clipboard.writeText(`${title}\n\n${url}`); } catch { /* silencieux */ }
    toast.success(t("blog.share_email_hint"));
    setOpen(false);
  };

  // Positionne le panneau : au-dessus du bouton si la place le permet, sinon en
  // dessous ; jamais hors de l'écran (marge de 8 px).
  const placeMenu = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(Math.max(8, r.right - MENU_W), Math.max(8, vw - MENU_W - 8));
    const estH = Math.min(vh * 0.7, 420);
    if (r.top > estH + 16) {
      setPos({ bottom: Math.round(vh - r.top + 8), left: Math.round(left) });
    } else {
      setPos({ top: Math.round(r.bottom + 8), left: Math.round(left) });
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-sharemenu-panel]")) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = () => setOpen(false); // le panneau est fixe : on le ferme au défilement
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  // La carte entière est un lien : chaque clic dans le menu doit rester local
  const hold = (e) => { e.preventDefault(); e.stopPropagation(); };

  const copyLink = async (e) => {
    hold(e);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    toast.success(t("blog.share_copied"));
    setTimeout(() => { setCopied(false); setOpen(false); }, 1200);
  };

  const nativeShare = async (e) => {
    hold(e);
    setOpen(false);
    try {
      await navigator.share({ title, text: title, url });
    } catch { /* annulé par l'utilisateur */ }
  };

  const ITEM = "flex items-center gap-3 w-full px-4 py-2.5 text-sm text-ivory/80 hover:text-gold hover:bg-gold/10 transition-colors duration-200 text-left";
  const items = [
    canNativeShare && { id: "native", label: t("blog.share_native"), icon: Share2, onClick: nativeShare },
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    // Messenger : ouvre directement la page de l'entreprise (demande du propriétaire) —
    // le lien fb-messenger://share était cassé au mobile et générique au bureau.
    { id: "messenger", label: "Messenger", icon: MessagesSquare, href: "https://m.me/trophee.ca" },
    isMobile && { id: "sms", label: t("blog.share_sms"), icon: Smartphone, href: `sms:?&body=${enc(`${title} ${url}`)}` },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://api.whatsapp.com/send?text=${enc(`${title}\n${url}`)}` },
    { id: "x", label: "X (Twitter)", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}` },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { id: "email", label: t("blog.share_email"), icon: Mail, href: `mailto:?subject=${enc(title)}&body=${enc(`${title}\n\n${url}`)}`, onClick: emailShare },
  ].filter(Boolean);

  const panel = open && pos ? (
    <span
      data-sharemenu-panel="1"
      data-testid={`${testid}-menu`}
      className="fixed z-[9999] w-56 max-h-[70vh] overflow-y-auto rounded-xl border border-gold/25 bg-surface shadow-[0_12px_40px_rgba(0,0,0,0.55)] py-1.5 block"
      style={{ left: pos.left, ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }) }}
      onClick={hold}
    >
      <span className="block px-4 pt-1.5 pb-2 text-[10px] uppercase tracking-widest text-gold/80">{t("footer.share_page")}</span>
      {items.map((it) => {
        const Icon = it.icon;
        return it.href ? (
          <a key={it.id} href={it.href} target="_blank" rel="noreferrer" onClick={it.onClick ? it.onClick : hold}
            data-testid={`${testid}-${it.id}`} className={ITEM}>
            <Icon className="w-4 h-4 shrink-0" /> {it.label}
          </a>
        ) : (
          <button key={it.id} type="button" onClick={it.onClick} data-testid={`${testid}-${it.id}`} className={ITEM}>
            <Icon className="w-4 h-4 shrink-0" /> {it.label}
          </button>
        );
      })}
      <button type="button" onClick={copyLink} data-testid={`${testid}-copy`} className={ITEM}>
        {copied ? <Check className="w-4 h-4 shrink-0 text-emerald-400" /> : <Link2 className="w-4 h-4 shrink-0" />}
        {copied ? t("blog.share_copied") : t("blog.share_copy")}
      </button>
    </span>
  ) : null;

  return (
    <span ref={ref} className="relative shrink-0" data-testid={testid}>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        title={ts("footer.share_page")}
        aria-expanded={open}
        onClick={(e) => { hold(e); if (!open) placeMenu(); setOpen((o) => !o); }}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors duration-300 ${
          open ? "bg-gold text-ink border-gold" : "border-ivory/20 text-ivory/65 hover:border-gold/60 hover:text-gold"
        }`}
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>
      {typeof document !== "undefined" ? createPortal(panel, document.body) : null}
    </span>
  );
}
